import { html, cx, useState, useEffect, useRef, useMemo, useCallback } from '../h.js'
import { Icon } from '../icons.js'
import { t, plural } from '../i18n.js'
import { dayLabel, sameDay, shortId, copyText } from '../format.js'
import { Avatar, Empty, Badges, useMenu, useToast } from '../components.js'
import { useApp, useStore, useRoom } from '../store.js'
import { MessageRow, PendingRow } from './message.js'
import { Composer } from './composer.js'
import { useBotCard, useAssistantAccess } from '../use-bot.js'
import { ROOM_TYPE, ROLE, ACTION } from '@pine/core'

const PAGE = 60

export function Conversation () {
  const store = useStore()
  const roomId = store.get('roomId')
  const room = useRoom(roomId)

  if (!roomId) {
    return html`
      <section class="conv">
        <${Empty} icon=${Icon.chat(44)} title=${t('conv.selectChat')} message=${t('conv.selectChatHint')} />
      </section>`
  }

  // A chat the directory named but this device holds no key for. There is no
  // room to render, so this is the chat's info: who is in it, and the one
  // button that can do something about the rest.
  const summary = store.client.getSummary(roomId)
  if (summary && summary.placeholder) {
    return html`<${RestoreScreen} key=${roomId} summary=${summary} />`
  }

  if (!room) {
    return html`<section class="conv"><div class="empty"><div class="spinner lg"></div></div></section>`
  }
  return html`<${ConversationBody} key=${roomId} room=${room} />`
}

/**
 * What a chat looks like before its contents have been asked for.
 *
 * After restoring an account the server can say what a room was called and who
 * was in it, and no more than that — it has never held a key. Everything here
 * is what is known; the button asks the people listed for the rest, and the
 * first one online answers with a way back in.
 */
function RestoreScreen ({ summary }) {
  const store = useStore()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [names, setNames] = useState({})

  const me = store.client.identity.id
  const others = (summary.members || []).filter((m) => m.account !== me)
  const asking = busy || store.client.isRestoring(summary.id)

  // Contacts on this device may already know some of these people by name.
  useEffect(() => {
    let alive = true
    Promise.all(others.map(async (m) => {
      const contact = await store.client.getContact(m.account).catch(() => null)
      return [m.account, (contact && contact.name) || '']
    })).then((pairs) => {
      if (alive) setNames(Object.fromEntries(pairs))
    })
    return () => { alive = false }
  }, [summary.id])

  async function restore () {
    setBusy(true)
    try {
      const { asked } = await store.client.restoreRoom(summary.id)
      if (asked === 0) toast(t('restore.nobodyOnline'), { error: true })
      else toast(t('restore.asked', { n: asked }))
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    } finally {
      setBusy(false)
    }
  }

  const label = (m) => names[m.account] ||
    (m.username ? '@' + m.username : shortId(m.account))

  return html`
    <section class="conv">
      <header class="conv-head">
        <button class="icon-btn narrow-only" aria-label=${t('onboard.back')}
                onClick=${() => store.closeRoom()}>${Icon.back(20)}</button>
        <${Avatar} name=${summary.name} id=${summary.id}
          square=${summary.type === ROOM_TYPE.CHANNEL} />
        <div class="conv-title">
          <h2>${summary.name || shortId(summary.id)}</h2>
          <div class="conv-sub">${plural('members', others.length + 1)}</div>
        </div>
      </header>

      <div class="pane-body" style="padding:24px 16px">
        <div class="empty" style="padding-top:8px">
          <div class="glyph">${Icon.database(38)}</div>
          <h3>${t('restore.title')}</h3>
          <p>${t('restore.hint')}</p>
          <button class="btn btn-primary btn-lg" disabled=${asking} onClick=${restore}>
            ${asking
              ? html`<div class="spinner"></div>`
              : html`${Icon.refresh(17)} ${t('restore.action')}`}
          </button>
          ${asking && html`<p class="small dim">${t('restore.asking')}</p>`}
        </div>

        ${others.length > 0 && html`
          <div class="section-title">${t('panel.members')}</div>
          ${others.map((m) => html`
            <div key=${m.account} class="list-row" style="cursor:default">
              <${Avatar} name=${label(m)} id=${m.account} size="sm" />
              <span class="body">
                <span class="title">${label(m)}</span>
                <span class="sub mono">${shortId(m.account)}</span>
              </span>
            </div>`)}`}
      </div>
    </section>`
}

function ConversationBody ({ room }) {
  const store = useApp()
  const toast = useToast()
  const menu = useMenu()
  const scroller = useRef(null)
  const atBottom = useRef(true)

  const [messages, setMessages] = useState([])
  const [members, setMembers] = useState([])
  const [meta, setMeta] = useState(null)
  const [reactions, setReactions] = useState({})
  const [limit, setLimit] = useState(PAGE)
  const [pinned, setPinned] = useState([])
  const [canPost, setCanPost] = useState(true)
  const [replyTo, setReplyTo] = useState(null)
  const [editing, setEditing] = useState(null)
  const [showJump, setShowJump] = useState(false)
  const [peerBadges, setPeerBadges] = useState(null)
  const [readStates, setReadStates] = useState({})
  // Messages sent from here that are not in the timeline yet (see Composer).
  const [pending, setPending] = useState([])
  const [loaded, setLoaded] = useState(false)

  // A one-to-one chat whose invite has not reached the other person yet. It is
  // not an error and needs no action — it just has not happened yet, and a
  // chat that says nothing about it looks broken.
  const summary = store.client.getSummary(room.id)
  const waitingFor = summary && summary.peer && store.client.directPending(summary.peer)
    ? (summary.name || t('conv.thatPerson'))
    : null

  // Anya's chat: whether this account may talk to her decides what goes where
  // the message field would be.
  const isAssistant = !!(summary && summary.peer && store.client.assistant &&
    store.client.assistant.isAssistant(summary.peer))
  const assistantAllowed = useAssistantAccess(store, isAssistant)

  // Which badges the other person in a direct chat carries.
  useEffect(() => {
    let live = true
    setPeerBadges(null)
    if (summary && summary.peer) {
      store.client.badgesFor(summary.peer).then((b) => { if (live) setPeerBadges(b) }).catch(() => {})
    }
    return () => { live = false }
  }, [summary && summary.peer])

  /* --- load and refresh the view --- */

  const refresh = useCallback(async () => {
    try {
      const [list, memberList, roomMeta, pins, allowed, reads] = await Promise.all([
        room.view.messages({ limit }),
        room.view.members(),
        room.view.meta(),
        room.view.pinned(),
        room.can(ACTION.POST),
        room.view.readStates()
      ])
      const reactionMap = await room.view.reactionsFor(list.map((m) => m.id))
      // In a chat with somebody blocked, nothing they wrote after the block.
      const record = store.client.getSummary(room.id)
      const peer = record && record.type === ROOM_TYPE.DM ? record.peer : null
      const since = peer && store.client.isBlocked(peer) ? store.client.blockedSince(peer) : 0
      setMessages(since ? list.filter((m) => !(m.author === peer && m.timestamp > since)) : list)
      setMembers(memberList)
      setMeta(roomMeta)
      setPinned(pins)
      setReactions(reactionMap)
      setCanPost(allowed)
      setReadStates(reads)
      setLoaded(true)
    } catch { /* room closed */ }
  }, [room, limit])

  // What waits to be sent while the room has not let this device in yet
  // (Room.seekAdmission): kept by the room, so it is still shown after the
  // chat was left and opened again.
  const [queued, setQueued] = useState(() => (room.queuedMessages ? room.queuedMessages() : []))

  useEffect(() => {
    refresh()
    const requeue = () => setQueued(room.queuedMessages ? room.queuedMessages() : [])
    requeue()
    const offs = [
      room.on('update', refresh),
      store.client.on('blocked-changed', refresh),
      room.on('meta', refresh),
      room.on('outbox', requeue),
      room.on('admitted', requeue)
    ]
    return () => offs.forEach((off) => off())
  }, [refresh, room])

  const onPending = useCallback((change) => {
    if (change.add) atBottom.current = true
    setPending((list) => {
      if (change.add) return [...list, change.add]
      if (change.sent) return list.map((p) => p.key === change.sent ? { ...p, id: change.id, sentAt: Date.now() } : p)
      if (change.drop) return list.filter((p) => p.key !== change.drop)
      return list
    })
  }, [])

  // Sent, and now in the timeline: the real message takes over. One that never
  // turns up — the room would not take it — stops being shown after a while.
  useEffect(() => {
    if (!pending.some((p) => p.id)) return
    const shown = new Set(messages.map((m) => m.id))
    const stale = Date.now() - 20000
    const keep = pending.filter((p) => !(p.id && (shown.has(p.id) || p.sentAt < stale)))
    if (keep.length !== pending.length) setPending(keep)
    const timer = setTimeout(() => setPending((list) => list.filter((p) => !(p.id && p.sentAt < Date.now() - 20000))), 21000)
    return () => clearTimeout(timer)
  }, [messages, pending])

  const shownIds = new Set(messages.map((m) => m.id))
  const outgoing = pending.filter((p) => p.roomId === room.id && !(p.id && shownIds.has(p.id)))
  for (const item of queued) {
    if (!outgoing.some((p) => p.key === item.key)) {
      outgoing.push({ key: item.key, roomId: room.id, text: item.text, files: item.files, at: item.at, queued: true })
    }
  }

  /* --- scroll behaviour --- */

  useEffect(() => {
    const node = scroller.current
    if (!node) return
    if (atBottom.current) node.scrollTop = node.scrollHeight
  }, [messages.length, outgoing.length])

  useEffect(() => {
    const node = scroller.current
    if (!node) return
    node.scrollTop = node.scrollHeight
    const onScroll = () => {
      const distance = node.scrollHeight - node.scrollTop - node.clientHeight
      atBottom.current = distance < 120
      setShowJump(distance > 400)
      if (node.scrollTop < 80 && messages.length >= limit) setLimit((n) => n + PAGE)
    }
    node.addEventListener('scroll', onScroll, { passive: true })
    return () => node.removeEventListener('scroll', onScroll)
  }, [room, messages.length, limit])

  // Mark read whenever the tail is visible and the window has focus.
  useEffect(() => {
    if (!atBottom.current || !messages.length) return
    const id = setTimeout(() => room.markRead().catch(() => {}), 600)
    return () => clearTimeout(id)
  }, [room, messages.length])

  const nameOf = useCallback((account) => {
    if (account === room.me) return t('chats.you')
    const member = members.find((m) => m.account === account)
    return (member && member.name) || shortId(account)
  }, [members, room.me])

  function onAction (action, message) {
    switch (action) {
      case 'reply':
        setEditing(null)
        setReplyTo(message)
        break
      case 'edit':
        setReplyTo(null)
        setEditing(message)
        break
      case 'pin':
        room.pin(message.id).catch((e) => toast(e.message, { error: true }))
        break
      case 'forward':
        store.showDialog({ type: 'forward', roomId: room.id, messageId: message.id })
        break
    }
  }

  /*
   * How far everyone else has read.
   *
   * Each member publishes a marker carrying the timestamp of the last message
   * they have seen, so a message of mine is read by all once the earliest of
   * those markers has passed it. A member who has never read anything holds
   * the watermark at zero, which is the honest answer — not everyone has seen
   * it. Until this existed the double tick was hard-coded to never show.
   */
  const readWatermark = useMemo(() => {
    const others = members.filter((m) => m.account !== room.me)
    if (others.length === 0) return 0
    let earliest = Infinity
    for (const member of others) {
      const state = readStates[member.account]
      earliest = Math.min(earliest, state ? state.at : 0)
      if (earliest === 0) break
    }
    return earliest === Infinity ? 0 : earliest
  }, [members, readStates, room.me])

  // A chat with a bot: its card says what it does and which commands it takes.
  const botCard = useBotCard(store, summary && summary.type === ROOM_TYPE.DM ? summary.peer : null)
  const isBot = !!botCard || !!(peerBadges && peerBadges.includes('bot'))
  const peerBlocked = !!(summary && summary.type === ROOM_TYPE.DM && summary.peer && store.client.isBlocked(summary.peer))

  const typing = room.typingAccounts.map(nameOf)
  const isChannel = meta && meta.type === ROOM_TYPE.CHANNEL
  const isDM = meta && meta.type === ROOM_TYPE.DM
  const isSaved = meta && meta.type === ROOM_TYPE.SAVED

  /*
   * A one-to-one chat is titled after the other person, and that name is only
   * known locally — the room's own metadata cannot hold it, because both ends
   * read the same metadata and would each see one of the two names. The
   * summary is where each device records who its chat is with.
   */
  const title = isSaved
    ? t('chats.saved')
    : isDM
      ? (summary && summary.name) || (summary && summary.peer ? shortId(summary.peer) : shortId(room.id))
      : (meta && meta.name) || shortId(room.id)

  const headerAvatar = isDM
    ? (summary && summary.avatar) || null
    : (meta && meta.avatar) || (summary && summary.avatar) || null

  const subtitle = typing.length
    ? (typing.length === 1 ? typing[0] + ' ' + t('conv.typing') : typing.slice(0, 2).join(', ') + ' ' + t('conv.typingMany'))
    : isSaved ? ''
      : isDM && isAssistant ? t('assistant.subtitle')
      : isDM && isBot ? t('bot.label')
      : isDM ? (room.onlineCount > 0 ? t('conv.online') : '')
        : [plural('members', members.length), room.onlineCount ? plural('online', room.onlineCount) : null]
            .filter(Boolean).join(' · ')

  return html`
    <section class="conv">
      <header class="conv-head">
        <button class="icon-btn narrow-only" aria-label=${t('onboard.back')}
                onClick=${() => store.closeRoom()}>${Icon.back(20)}</button>
        <${Avatar} name=${title} id=${(isDM && summary && summary.peer) || room.id}
          src=${headerAvatar} square=${isChannel}
          onClick=${() => store.set({ panel: true, mobileView: 'panel' })} />
        <div class="conv-title" onClick=${() => store.set({ panel: true, mobileView: 'panel' })}
             style="cursor:pointer">
          <h2>${title}${isDM && Badges({ badges: peerBadges, size: 17 })}</h2>
          <div class=${cx('conv-sub', typing.length && 'typing')}>${subtitle}</div>
        </div>

        ${!isSaved && !isBot && store.calls && store.calls.supported && html`
          <button class="icon-btn" title=${t('call.accept')}
                  onClick=${() => startCall(store, room, false, toast)}>${Icon.phone(20)}</button>
          <button class="icon-btn" title=${t('call.camera')}
                  onClick=${() => startCall(store, room, true, toast)}>${Icon.camera(20)}</button>`}

        <button class="icon-btn" onClick=${() => store.showDialog({ type: 'search', roomId: room.id })}>
          ${Icon.search(20)}
        </button>
        <button class=${cx('icon-btn', store.get('panel') && 'active')}
                onClick=${() => store.set({ panel: !store.get('panel'), mobileView: 'panel' })}>
          ${Icon.info(20)}
        </button>
      </header>

      ${pinned.length > 0 && html`
        <button class="row gap3" style="padding:8px 16px;border-bottom:1px solid var(--border);background:var(--surface)"
                onClick=${() => {
                  const node = document.querySelector('[data-id="' + pinned[0].message.id + '"]')
                  if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' })
                }}>
          <span style="color:var(--accent)">${Icon.pin(16)}</span>
          <span class="grow truncate small" style="text-align:left">
            <b>${t('conv.pinned')}</b> · ${pinned[0].message.text || '…'}
          </span>
          <span class="icon-btn" onClick=${(e) => { e.stopPropagation(); room.pin(pinned[0].message.id, { remove: true }) }}>
            ${Icon.close(15)}
          </span>
        </button>`}

      <div class="timeline" ref=${scroller}>
        ${messages.length >= limit && html`
          <div class="row center" style="padding:8px">
            <button class="btn btn-ghost btn-sm" onClick=${() => setLimit((n) => n + PAGE)}>
              ${t('conv.loadOlder')}
            </button>
          </div>`}

        ${waitingFor && html`
          <div class="notice" role="status">
            ${isAssistant ? t('assistant.connecting') : t('conv.awaitingPeer', { name: waitingFor })}
          </div>`}

        ${messages.length === 0 && outgoing.length === 0
          ? html`<${Empty} icon=${Icon.chat(38)} title=${t('conv.empty')} message=${t('conv.emptyHint')} />`
          : messages.map((message, i) => {
              const prev = messages[i - 1]
              const next = messages[i + 1]
              const showDay = !prev || !sameDay(prev.timestamp, message.timestamp)
              return html`
                ${showDay && html`
                  <div class="day-sep" key=${'d' + message.id}><span>${dayLabel(message.timestamp)}</span></div>`}
                <${MessageRow}
                  key=${message.id}
                  message=${message}
                  room=${room}
                  prev=${prev}
                  next=${next}
                  isMine=${message.author === room.me}
                  authorName=${nameOf(message.author)}
                  showAuthor=${!isDM && !isSaved}
                  reactions=${reactions[message.id]}
                  readByAll=${message.timestamp <= readWatermark}
                  onAction=${onAction}
                  menu=${menu} />`
            })}

        ${outgoing.map((item) => html`<${PendingRow} key=${item.key} item=${item} />`)}
      </div>

      ${showJump && html`
        <button class="btn btn-soft" style="position:absolute;right:24px;bottom:96px;border-radius:999px;box-shadow:var(--shadow-2)"
                onClick=${() => {
                  const node = scroller.current
                  if (node) node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
                }}>
          ${Icon.chevronDown(18)} ${t('conv.jumpToLatest')}
        </button>`}

      <${Composer}
        room=${room}
        canPost=${canPost && (room.writable || room.queueing)}
        replyTo=${replyTo}
        editing=${editing}
        bot=${botCard}
        startable=${isBot && !isAssistant && loaded && room.writable && outgoing.length === 0 &&
          !messages.some((m) => m.author === room.me)}
        notice=${peerBlocked ? html`
          <div>${t('block.inChat')}</div>
          <button class="btn btn-soft btn-sm" style="margin-top:8px" onClick=${async () => {
            await store.client.blockContact(summary.peer, false).catch(() => {})
            toast(t('block.undone', { name: title }))
          }}>${t('contacts.unblock')}</button>` : isAssistant && assistantAllowed === false ? html`
          <div>${t('assistant.denied')}</div>
          <div class="row gap2 center" style="margin-top:8px;flex-wrap:wrap">
            <button class="btn btn-soft btn-sm" onClick=${async () => {
              const ok = await copyText(store.client.identity.id)
              toast(ok ? t('assistant.idCopied') : t('err.generic'), { error: !ok })
            }}>${Icon.copy(15)} ${t('assistant.copyId')}</button>
            <button class="btn btn-primary btn-sm" onClick=${() => store.showDialog({ type: 'contact-dev' })}>
              ${Icon.mail(15)} ${t('settings.contactDev')}
            </button>
          </div>` : null}
        onPending=${onPending}
        onClearContext=${() => { setReplyTo(null); setEditing(null) }} />

      ${!room.writable && html`
        <div class="row center gap2 dim small" style="padding:6px 16px;flex-wrap:wrap;text-align:center">
          <div class="spinner" style="width:13px;height:13px"></div>${room.queueing ? t('conv.queueing') : t('conv.notWritable')}
          ${loaded && !store.client.isRestoring(room.id) && html`
            <button class="btn btn-ghost btn-sm" onClick=${() => askForAccess(store, room, toast)}>
              ${t('conv.askAccess')}
            </button>`}
        </div>`}

      ${menu.node}
    </section>`
}

/*
 * A chat this device can read but not write in — restored onto a new device,
 * and nobody in it has let the new device in yet. The app keeps asking on its
 * own; this is for not having to wait for its next try.
 */
async function askForAccess (store, room, toast) {
  try {
    const { asked, already } = await store.client.restoreRoom(room.id)
    if (already) return
    if (asked === 0) toast(t('restore.nobodyOnline'), { error: true })
    else toast(t('restore.asked', { n: asked }))
  } catch (err) {
    toast(err.message || t('err.generic'), { error: true })
  }
}

async function startCall (store, room, video, toast) {
  try {
    const call = await store.calls.startCall(room.id, { video })
    // In a group the button joins whatever is already running rather than
    // starting a second call beside it — worth saying, since the person
    // pressed "call" and got somebody else's call.
    if (call && !call.initiator) toast(t('call.joining'))
  } catch (err) {
    toast(err.message || t('err.generic'), { error: true })
  }
}

export { ROLE }
